import React, { useEffect, useRef, useState } from "react";

/* Mirrors ApiClientController's MaxClientNameLength / MaxNotesLength so the
 * UI rejects the same input the server would, before a round-trip. */
const MAX_CLIENT_NAME_LENGTH = 100;
const MAX_NOTES_LENGTH = 500;

export const DialogShell = ({ title, onClose, children, size = "md" }) => {
  const cardRef = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    const focusable = cardRef.current?.querySelector(
      "input, select, textarea, button",
    );
    if (focusable) focusable.focus();

    const onKey = e => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("api-key-manager-dialog-open");

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("api-key-manager-dialog-open");
      if (previouslyFocused.current && previouslyFocused.current.focus) {
        previouslyFocused.current.focus();
      }
    };
  }, [onClose]);

  return (
    <div
      className="api-key-manager-dialog-backdrop"
      role="presentation"
      /* Explicit no-op on backdrop click. Users must use a dialog
       * action button to close. */
    >
      <div
        ref={cardRef}
        className={`api-key-manager-dialog api-key-manager-dialog--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="api-key-manager-dialog-title"
      >
        <div className="api-key-manager-dialog__header">
          <div
            id="api-key-manager-dialog-title"
            className="api-key-manager-dialog__title"
            role="heading"
            aria-level={2}
          >
            {title}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};

/* ==================================================================
 * Add
 * ================================================================== */
export const AddClientDialog = ({ onSubmit, onClose }) => {
  const [clientName, setClientName] = useState("");
  const [notes, setNotes] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async e => {
    e.preventDefault();
    setError("");
    const trimmedName = clientName.trim();
    if (!trimmedName) {
      setError("Client name is required.");
      return;
    }
    if (trimmedName.length > MAX_CLIENT_NAME_LENGTH) {
      setError(
        `Client name must be ${MAX_CLIENT_NAME_LENGTH} characters or fewer.`,
      );
      return;
    }
    if (notes.trim().length > MAX_NOTES_LENGTH) {
      setError(`Notes must be ${MAX_NOTES_LENGTH} characters or fewer.`);
      return;
    }
    if (expiresOn) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (new Date(expiresOn) <= today) {
        setError("Expiry must be a future date.");
        return;
      }
    }
    setBusy(true);
    try {
      await onSubmit({
        clientName: trimmedName,
        notes: notes.trim(),
        expiresOn: expiresOn || null,
      });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell title="Add API Client" onClose={onClose}>
      <form className="api-key-manager-dialog__body" onSubmit={submit}>
        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">
            Client name{" "}
            <span className="api-key-manager-dialog__required">*</span>
          </span>
          <input
            type="text"
            value={clientName}
            onChange={e => setClientName(e.target.value)}
            maxLength={MAX_CLIENT_NAME_LENGTH}
            required
            disabled={busy}
          />
        </label>

        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">Notes</span>
          <textarea
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            maxLength={MAX_NOTES_LENGTH}
            disabled={busy}
          />
        </label>

        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">
            Expires on (optional)
          </span>
          <input
            type="date"
            value={expiresOn}
            onChange={e => setExpiresOn(e.target.value)}
            min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
            disabled={busy}
          />
        </label>

        {error && (
          <p className="api-key-manager-dialog__error" role="alert">
            {error}
          </p>
        )}

        <div className="api-key-manager-dialog__actions">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy}
          >
            {busy ? "Creating\u2026" : "Create client"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
};

/* ==================================================================
 * Update
 * ================================================================== */
export const UpdateClientDialog = ({ client, busy, onSubmit, onClose }) => {
  const [clientName, setClientName] = useState(client.clientName || "");
  const [notes, setNotes] = useState(client.notes || "");
  const [expiresOn, setExpiresOn] = useState(
    client.expiresOn ? String(client.expiresOn).slice(0, 10) : "",
  );
  const [isActive, setIsActive] = useState(client.isActive !== false);
  const [error, setError] = useState("");

  const submit = async e => {
    e.preventDefault();
    setError("");
    const trimmedName = clientName.trim();
    if (!trimmedName) {
      setError("Client name is required.");
      return;
    }
    if (trimmedName.length > MAX_CLIENT_NAME_LENGTH) {
      setError(
        `Client name must be ${MAX_CLIENT_NAME_LENGTH} characters or fewer.`,
      );
      return;
    }
    if (notes.trim().length > MAX_NOTES_LENGTH) {
      setError(`Notes must be ${MAX_NOTES_LENGTH} characters or fewer.`);
      return;
    }
    try {
      await onSubmit({
        clientName: trimmedName,
        notes: notes.trim(),
        isActive,
        expiresOn: expiresOn || null,
      });
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  return (
    <DialogShell title="Update API Client" onClose={onClose}>
      <form className="api-key-manager-dialog__body" onSubmit={submit}>
        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">
            Client name{" "}
            <span className="api-key-manager-dialog__required">*</span>
          </span>
          <input
            type="text"
            value={clientName}
            onChange={e => setClientName(e.target.value)}
            maxLength={MAX_CLIENT_NAME_LENGTH}
            required
            disabled={busy}
          />
        </label>

        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">Notes</span>
          <textarea
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            maxLength={MAX_NOTES_LENGTH}
            disabled={busy}
          />
        </label>

        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">
            Expires on (optional)
          </span>
          <input
            type="date"
            value={expiresOn}
            onChange={e => setExpiresOn(e.target.value)}
            disabled={busy}
          />
        </label>

        <label className="api-key-manager-dialog__field api-key-manager-dialog__field--inline">
          <input
            type="checkbox"
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            disabled={busy}
          />
          <span className="api-key-manager-dialog__label">Active</span>
        </label>

        {error && (
          <p className="api-key-manager-dialog__error" role="alert">
            {error}
          </p>
        )}

        <div className="api-key-manager-dialog__actions">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy}
          >
            {busy ? "Saving\u2026" : "Save changes"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
};

/* ==================================================================
 * Secret reveal (one-time)
 * ================================================================== */
export const SecretRevealDialog = ({
  name,
  clientId,
  clientSecret,
  onClose,
}) => {
  const [copiedField, setCopiedField] = useState(null);

  const copy = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      window.setTimeout(() => setCopiedField(null), 1500);
    } catch (e) {
      /* clipboard blocked; silently fail */
    }
  };

  return (
    <DialogShell
      title="API client created"
      onClose={onClose}
      size="lg"
    >
      <div className="api-key-manager-dialog__body api-key-manager-dialog__body--reveal">
        <div
          className="api-key-manager-dialog__success"
          role="status"
          aria-live="polite"
        >
          <span
            className="api-key-manager-dialog__success-icon"
            aria-hidden="true"
          >
            <svg viewBox="0 0 20 20" width="18" height="18">
              <circle cx="10" cy="10" r="9" fill="#007dbd" />
              <path
                d="M5.75 10.35 L8.6 13.2 L14.25 7.55"
                fill="none"
                stroke="#fff"
                strokeWidth="2.1"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="api-key-manager-dialog__success-title">
            <span className="api-key-manager-dialog__success-name">
              {name}
            </span>{" "}
            was created
          </span>
        </div>

        <p className="api-key-manager-dialog__lede">
          Copy your credentials now. The secret is shown only once and
          cannot be retrieved after this dialog is closed.
        </p>

        <div className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">Client ID</span>
          <div className="api-key-manager-dialog__copy-row">
            <code className="api-key-manager-dialog__code">{clientId}</code>
            <button
              type="button"
              className="btn api-key-manager-dialog__copy-btn"
              onClick={() => copy("clientId", clientId)}
            >
              {copiedField === "clientId" ? "Copied" : "Copy"}
            </button>
          </div>
        </div>

        <div className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">
            Client Secret
          </span>
          <div className="api-key-manager-dialog__copy-row">
            <code className="api-key-manager-dialog__code">
              {clientSecret}
            </code>
            <button
              type="button"
              className="btn api-key-manager-dialog__copy-btn"
              onClick={() => copy("clientSecret", clientSecret)}
            >
              {copiedField === "clientSecret" ? "Copied" : "Copy"}
            </button>
          </div>
        </div>

        <div className="api-key-manager-dialog__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </DialogShell>
  );
};

/* ==================================================================
 * Revoke confirm
 * ================================================================== */
export const RevokeConfirmDialog = ({ client, busy, onConfirm, onClose }) => (
  <DialogShell title="Revoke API client?" onClose={onClose}>
    <div className="api-key-manager-dialog__body">
      <p>
        Revoke <strong>{client.clientName}</strong>? External systems
        using this credential will immediately stop being able to
        authenticate.
      </p>
      <p className="api-key-manager-dialog__muted">
        You can reactivate the client later by editing it and enabling
        &ldquo;Active&rdquo;.
      </p>
      <div className="api-key-manager-dialog__actions">
        <button
          type="button"
          className="btn"
          onClick={onClose}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn api-key-manager-dialog__btn-danger"
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? "Revoking\u2026" : "Revoke client"}
        </button>
      </div>
    </div>
  </DialogShell>
);

/* ==================================================================
 * Email secret
 * -----------------------------------------------------------------
 * Sends the client secret to an admin-supplied email address via the
 * backend "reshare" endpoint. The secret leaves the browser only as
 * a recipient reference; the server pulls the stored value and emails
 * it directly, so no secret material is ever displayed here.
 * ================================================================== */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EmailSecretDialog = ({ client, busy, onSubmit, onClose }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const submit = async e => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Recipient email address is required.");
      return;
    }
    if (!EMAIL_PATTERN.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    try {
      await onSubmit(trimmed);
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  return (
    <DialogShell title="Email client secret" onClose={onClose}>
      <form className="api-key-manager-dialog__body" onSubmit={submit}>
        <p>
          Send the credentials for <strong>{client.clientName}</strong> to
          the recipient below.
        </p>

        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">
            Recipient email{" "}
            <span className="api-key-manager-dialog__required">*</span>
          </span>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="email"
            required
            disabled={busy}
          />
        </label>

        {error && (
          <p className="api-key-manager-dialog__error" role="alert">
            {error}
          </p>
        )}

        <div className="api-key-manager-dialog__actions">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy}
          >
            {busy ? "Sending\u2026" : "Send secret"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
};
